/**
 * This file is to be used for mocking the data returned from the PageConfigComponentsQuery.
 * When developers want to test new display components that will eventually be added to unified-home, they can add the component to this file. Then, add the component id to the ComponentsConfigRenderer.tsx file.
 *
 * To be in the Unified Home Sandbox mode, override yourself into this epic experiment: https://tools.coursera.org/epic/experiment/WggFk5t4Ee6zwxJ4Zk1IsQ
 */
import { PageConfig_PageName as PageName } from '__generated__/graphql-types';

import type { PageConfig_ComponentConfigFragment as PageConfigComponent } from 'bundles/page-config-common/queries/page-config/__generated__/PageConfigComponentsQuery';

export const PageConfigMockData: Partial<Record<PageName, PageConfigComponent[]>> = {
  [PageName.ConsumerLoggedInHome]: [
    {
      id: 'PageConfig_StandardRolesCollection',
      __typename: 'PageConfig_StandardRolesCollection',
    },
    {
      id: 'PageConfig_CoachOnboardingBanner',
      __typename: 'PageConfig_CoachOnboardingBanner',
    },
    {
      id: 'PageConfig_CareerRolesComponent',
      __typename: 'PageConfig_CareerRolesComponent',
    },
    {
      id: 'PageConfig_PathwayRecommendationsComponent',
      __typename: 'PageConfig_PathwayRecommendationsComponent',
    },
    {
      id: 'PageConfig_OnboardingResultsHeaderComponent',
      __typename: 'PageConfig_OnboardingResultsHeaderComponent',
    },
    // Sandbox Test Component example for developers to use
    {
      id: 'PageConfig_SandboxTestComponent',
      __typename: 'PageConfig_SandboxTestComponent',
    },
    {
      id: 'PageConfig_EnrolledProductsComponent',
      __typename: 'PageConfig_EnrolledProductsComponent',
    },
    {
      id: 'degrees',
      __typename: 'PageConfig_PremiumCollectionComponent',
    },
    {
      id: 'ALGOLIA_BASED:mostPaidEnrollmentsNumericTag~most-paid-algolia',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:recently-viewed-xdps',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-career-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'CURATED:87zOiIzzRZ6ToW93nxgljg',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'PageConfig_PromoUnitsComponent',
      __typename: 'PageConfig_PromoUnitsComponent',
    },
    {
      id: 'FULLY_PERSONALIZED:s12n-dnn',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-skill-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'PageConfig_DomainPillsComponent',
      __typename: 'PageConfig_DomainPillsComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-career-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'FULLY_PERSONALIZED:rhyme-dnn',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'CURATED:0KgW9WEkQdi-LlCpmnW4Vw',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'mastertracks',
      __typename: 'PageConfig_PremiumCollectionComponent',
    },
    {
      id: 'FULLY_PERSONALIZED:course-dnn',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-career-industry-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-profile-skill-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'CURATED:iVJQYo8xRGOUr02woGCLNg',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:popular-derived-domain',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:basic-career-derived-skill',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-industry-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-coursera-plus-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-recent-view-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
    {
      id: 'SEMI_PERSONALIZED:derived-day0-free-real-time-rec-algo',
      __typename: 'PageConfig_CollectionComponent',
    },
  ] as PageConfigComponent[],
  [PageName.EnterpriseHome]: [
    {
      id: 'PageConfig_OnboardingResultsHeaderComponent',
      __typename: 'PageConfig_OnboardingResultsHeaderComponent',
    },
    // Sandbox Test Component example for developers to use
    {
      id: 'PageConfig_SandboxTestComponent',
      __typename: 'PageConfig_SandboxTestComponent',
    },
    {
      id: 'PageConfig_EnrolledProductsComponent',
      __typename: 'PageConfig_EnrolledProductsComponent',
    },
    {
      id: 'PageConfig_EnterpriseAssignedCourses',
      __typename: 'PageConfig_EnterpriseAssignedCourses',
    },
    {
      id: 'PageConfig_EnterpriseSkillClipsCollectionComponent',
      __typename: 'PageConfig_EnterpriseSkillClipsCollectionComponent',
    },
    {
      id: 'PageConfig_EnterpriseForCreditCollectionComponent',
      __typename: 'PageConfig_EnterpriseForCreditCollectionComponent',
    },
    {
      id: 'PageConfig_SkillsFocusComponent',
      __typename: 'PageConfig_SkillsFocusComponent',
    },
    {
      id: 'PageConfig_SkillSetCardsComponent',
      __typename: 'PageConfig_SkillSetCardsComponent',
    },
    {
      id: 'PageConfig_EnterpriseCuratedCollectionComponent',
      __typename: 'PageConfig_EnterpriseCuratedCollectionComponent',
    },
    {
      id: 'PageConfig_EnterpriseBrowseCollectionComponent',
      __typename: 'PageConfig_EnterpriseBrowseCollectionComponent',
    },
    {
      id: 'PageConfig_DomainPillsComponent',
      __typename: 'PageConfig_DomainPillsComponent',
    },
  ] as PageConfigComponent[],
  [PageName.ConsumerSearch]: [
    // 1st group: ["isFreeQuery","isLoggedIn","hasDesiredRole"]
    {
      id: 'PageConfig_BrowseValuePropositionComponent',
      __typename: 'PageConfig_BrowseValuePropositionComponent',
    },
    {
      id: 'PageConfig_MostPopularFreeInRoleCollectionComponent',
      __typename: 'PageConfig_MostPopularFreeInRoleCollectionComponent',
    },
    {
      id: 'PageConfig_ExploreRolesComponent',
      __typename: 'PageConfig_ExploreRolesComponent',
    },
    {
      id: 'PageConfig_SearchFrameComponent',
      __typename: 'PageConfig_SearchFrameComponent',
    },
    // 2nd group: ["isFreeQuery","isLoggedIn"]
    {
      id: 'PageConfig_CoachOnboardingBanner',
      __typename: 'PageConfig_CoachOnboardingBanner',
    },
    {
      id: 'PageConfig_MostPopularFreeCollectionComponent',
      __typename: 'PageConfig_MostPopularFreeCollectionComponent',
    },
    {
      id: 'PageConfig_PathwayFreeTrialCollectionComponent',
      __typename: 'PageConfig_PathwayFreeTrialCollectionComponent',
    },
    // 3rd group: ["isFreeQuery"]
    {
      id: 'PageConfig_ProgressiveOnboardingComponent',
      __typename: 'PageConfig_ProgressiveOnboardingComponent',
    },
    // 4th group: []
    // (SearchFrameComponent already included above)
  ] as PageConfigComponent[],
  [PageName.BrowseEqpFree]: [
    {
      id: 'PageConfig_BrowseValuePropositionComponent',
      __typename: 'PageConfig_BrowseValuePropositionComponent',
    },
    {
      id: 'PageConfig_MostPopularFreeCollectionComponent',
      __typename: 'PageConfig_MostPopularFreeCollectionComponent',
    },
    {
      id: 'PageConfig_ExploreRolesComponent',
      __typename: 'PageConfig_ExploreRolesComponent',
    },
    {
      id: 'PageConfig_SkillsPillsComponent',
      __typename: 'PageConfig_SkillsPillsComponent',
    },
    {
      id: 'PageConfig_PathwayFreeTrialCollectionComponent',
      __typename: 'PageConfig_PathwayFreeTrialCollectionComponent',
    },
    {
      id: 'PageConfig_ProgressiveOnboardingComponent',
      __typename: 'PageConfig_ProgressiveOnboardingComponent',
    },
    {
      id: 'PageConfig_SearchFrameComponent',
      __typename: 'PageConfig_SearchFrameComponent',
    },
    {
      id: 'PageConfig_CategoriesPillsComponent',
      __typename: 'PageConfig_CategoriesPillsComponent',
    },
    {
      id: 'PageConfig_TopPartnersComponent',
      __typename: 'PageConfig_TopPartnersComponent',
    },
    {
      id: 'PageConfig_FAQComponent',
      __typename: 'PageConfig_FAQComponent',
    },
    {
      id: 'PageConfig_UpsellComponent',
      __typename: 'PageConfig_UpsellComponent',
    },
  ] as PageConfigComponent[],
};
